
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CAL_BOOKING_URL } from "@shared/demo-booking";
import { useBranding } from "@/components/BrandingProvider";
import { useTheme } from "@/components/ThemeProvider";
import { useAuthDialog } from "@/components/landing/AuthDialog";
import { AuthStorage } from "@/lib/auth-storage";
import { scrollToSection } from "@/lib/section-nav";
import { FREE_VOICE_CREDITS } from "@/lib/free-credits";
import { AnnouncementBar } from "@/components/landing/AnnouncementBar";
import { LanguageSelector } from "@/components/LanguageSelector";

const navLinks = [
  { href: "#capabilities", label: "Features", key: "features" },
  // "#testimonials" used to sit in this list pointing at a section no page renders.
  { href: "#platform", label: "Platform", key: "platform" },
  { href: "#use-cases", label: "Use Cases", key: "useCases" },
  { href: "#pricing", label: "Pricing", key: "pricing" },
  { href: "/blog", label: "Blog", key: "blog" },
  { href: "#contact", label: "Contact", key: "contact" },
];

export function Navbar() {
  const { t } = useTranslation("landing");
  const [location, setLocation] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const previousOverflow = useRef<string>("");
  const { branding, currentLogo } = useBranding();
  const { theme, setTheme } = useTheme();
  const { openAuth } = useAuthDialog();
  const isAuthenticated = AuthStorage.isAuthenticated();
  const isSuperAdmin = AuthStorage.isSuperAdmin();

  const restoreBodyOverflow = useCallback(() => {
    document.body.style.overflow = previousOverflow.current || "unset";
  }, []);

  const lockBodyOverflow = useCallback(() => {
    previousOverflow.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      lockBodyOverflow();
    } else {
      restoreBodyOverflow();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      restoreBodyOverflow();
    };
  }, [isMobileMenuOpen, lockBodyOverflow, restoreBodyOverflow]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith("#")) {
      if (location !== "/") {
        // Off the landing page: carry the hash over and let the landing page
        // scroll once it has rendered (useSectionHashScroll).
        setLocation("/" + href);
      } else {
        scrollToSection(href);
      }
    } else {
      setLocation(href);
    }
  };

  const handleGetFreeCredits = () => {
    setIsMobileMenuOpen(false);
    if (isAuthenticated) {
      window.location.href = isSuperAdmin ? "/admin" : "/app";
      return;
    }
    openAuth("register");
  };

  const handleSignIn = () => {
    setIsMobileMenuOpen(false);
    if (isAuthenticated) {
      window.location.href = isSuperAdmin ? "/admin" : "/app";
    } else {
      openAuth("login", true);
    }
  };

  const handleGetStarted = () => {
    setIsMobileMenuOpen(false);
    if (isAuthenticated) {
      window.location.href = isSuperAdmin ? "/admin" : "/app";
    } else {
      window.open(CAL_BOOKING_URL, "_blank", "noopener,noreferrer");
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white dark:bg-slate-950 shadow-md border-b border-slate-200 dark:border-slate-800"
          : "bg-white dark:bg-slate-950"
      }`}
      data-site-header
      data-testid="navbar"
    >
      <AnnouncementBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/*
            The XenonLabs attribution sits UNDER the wordmark rather than beside
            it: at small widths the old side-by-side version was the first thing
            dropped, which is the opposite of what a parent-brand credit is for.
          */}
          <div className="flex flex-col justify-center gap-0.5">
            <a
              href="/"
              className="flex items-center transition-opacity hover:opacity-80"
              data-testid="link-logo"
              onClick={(e) => {
                e.preventDefault();
                setLocation("/");
              }}
            >
              <img
                src={theme === "dark" ? "/images/branding/voiceprologowhite.svg" : "/images/branding/voiceprologo.svg"}
                alt="VoicePro logo"
                className="h-8 w-auto object-contain sm:h-10 md:h-11"
                data-testid="img-logo"
              />
            </a>
            <a
              href="https://xenonlabs.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1 text-[10px] font-medium leading-none tracking-wide text-slate-500 transition-colors hover:text-violet-600 dark:text-slate-400 sm:text-xs"
              data-testid="link-xenonlabs-brand"
            >
              <span>{t("nav.productOf")}</span>
              <span className="font-bold text-slate-700 underline-offset-2 group-hover:text-violet-600 group-hover:underline dark:text-slate-200">
                XenonLabs.ai
              </span>
            </a>
          </div>

          <div className="hidden lg:flex items-center gap-5 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
                data-testid={`link-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
              >
                {t(`nav.${link.key}`)}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <Button
              variant="outline"
              onClick={handleSignIn}
              data-testid="button-nav-login"
              className="h-10 rounded-lg border-slate-300 bg-white px-4 xl:px-6 font-semibold text-slate-800 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-violet-700 dark:hover:bg-violet-950/50 dark:hover:text-violet-300"
            >
              {isAuthenticated ? t("nav.dashboard") : t("nav.signIn")}
            </Button>
            {!isAuthenticated && (
              /*
                The offer rides ON the Sign Up button rather than beside it: it
                is the reason to press that button, and a separate CTA competed
                with it for the same click.
              */
              <div className="relative">
                <span
                  className="pointer-events-none absolute -top-2.5 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-md"
                  data-testid="badge-nav-free-credits"
                >
                  {t("nav.freeCreditsBadge", { count: FREE_VOICE_CREDITS })}
                </span>
                <Button
                  onClick={handleGetFreeCredits}
                  data-testid="button-nav-free-credits"
                  className="group h-10 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 px-4 xl:px-6 font-semibold text-white shadow-lg shadow-violet-600/25 transition-all hover:from-violet-500 hover:to-purple-500"
                >
                  {t("nav.signUp")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(true)}
            data-testid="button-mobile-menu-open"
            aria-label={t("nav.openMenu")}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              data-testid="mobile-menu-overlay"
            />

            <motion.div
              ref={mobileMenuRef}
              className="fixed top-0 right-0 bottom-0 w-full max-w-sm h-screen min-h-screen bg-white dark:bg-slate-950 z-50 lg:hidden shadow-2xl border-l border-border"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              data-testid="mobile-menu"
            >
              <div className="flex flex-col h-screen min-h-screen bg-white dark:bg-slate-950">
                <div className="shrink-0 flex items-center justify-between h-20 px-4 border-b bg-white dark:bg-slate-950">
                  <div className="flex items-center">
                    <img
                      src={theme === "dark" ? "/images/branding/voiceprologowhite.svg" : "/images/branding/voiceprologo.svg"}
                      alt="VoicePro logo"
                      className="h-9 sm:h-10 w-auto object-contain"
                      data-testid="img-mobile-logo"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid="button-mobile-menu-close"
                    aria-label={t("nav.closeMenu")}
                  >
                    <X className="h-6 w-6" />
                  </Button>
                </div>

                <nav className="flex-1 overflow-y-auto px-4 py-6 bg-white dark:bg-slate-950">
                  <div className="flex flex-col gap-2">
                    {navLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        className="block text-base font-medium py-3 px-4 rounded-lg text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                        data-testid={`link-mobile-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(link.href);
                        }}
                      >
                        {t(`nav.${link.key}`)}
                      </a>
                    ))}
                  </div>
                </nav>

                <div className="shrink-0 p-4 border-t bg-white dark:bg-slate-950 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                      {t("nav.language")}
                    </span>
                    <LanguageSelector />
                  </div>
                  {!isAuthenticated && (
                    <div className="relative pt-2">
                      <span
                        className="pointer-events-none absolute -top-0.5 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-md"
                        data-testid="badge-mobile-free-credits"
                      >
                        {t("nav.freeCreditsBadge", { count: FREE_VOICE_CREDITS })}
                      </span>
                      <Button
                        className="h-11 w-full rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 font-semibold text-white shadow-lg shadow-violet-600/25 transition-all hover:from-violet-500 hover:to-purple-500"
                        onClick={handleGetFreeCredits}
                        data-testid="button-mobile-free-credits"
                      >
                        {t("nav.signUp")}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    className="h-11 w-full rounded-lg border-slate-300 font-semibold transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 dark:border-slate-700"
                    onClick={handleSignIn}
                    data-testid="button-mobile-login"
                  >
                    {isAuthenticated ? t("nav.dashboard") : t("nav.signIn")}
                  </Button>
                  <a
                    href="https://xenonlabs.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-xs text-slate-500 transition-colors hover:text-violet-600"
                  >
                    <span>{t("nav.productOf")}</span>
                    <span className="font-bold text-slate-700 dark:text-slate-200">XenonLabs.ai</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Navbar;
